import { useState, useCallback, useMemo } from "react";
import { useForm, RegisterOptions } from "react-hook-form";
import { ImageUploader } from "@/components/uploader/ImageUploader";
import { NicheInputField } from "@/components/popups/NicheInputField";
import { showNotification } from "@/components/notifications/Notifications";
import { Checkbox } from "@/components/inputs/Checkbox";
import { TagManager } from "@/components/tags-selector/TagManager";
import "./NicheForm.scss";
import { Button } from "@/components/buttons/Button";

export interface NicheFields {
  id: "name" | "description" | "rules";
  label: string;
  placeholder: string;
  validation: RegisterOptions<{ name: string; description: string; rules: string }>;
  type?: string;
  rows?: number;
  disabled?: boolean;
  defaultValue: string;
}

export enum NicheFieldsNames {
  name="name",
  description="description",
}

export interface IPrefOption {
  label: string;
  name: string;
  value: string;
  isChecked: boolean;
}

interface INicheFormProps {
  avatarTicketId: string | null;
  bannerTicketId: string | null;
  onSubmit: (data: INicheForm) => void;
  handleAvatarTicketId: (ticketId: string) => void;
  handleBannerTicketId: (ticketId: string) => void;
  onSetPreferences: (prefs: IPrefOption[]) => void;
  onTagsChange: (tag: string[]) => void;
  tags: string[];
  fields: { [key in NicheFieldsNames]: NicheFields };
  btnTitle: string;
  preview?: string | null;
  prefOptions: IPrefOption[];
  banner?: string | null;
}

export interface INicheForm {
  name: string;
  description: string;
  rules: string;
}

const MAX_TAGS = 5;

export const NicheForm = (props: INicheFormProps) => {
  const {
    onSubmit,
    handleAvatarTicketId,
    handleBannerTicketId,
    onSetPreferences,
    onTagsChange,
    banner,
    avatarTicketId,
    bannerTicketId,
    fields,
    btnTitle,
    preview,
    prefOptions,
    tags
  } = props;

  const [avatarImgLoading, setAvatarImgLoading] = useState<number>(0);
  const [bannerImgLoading, setBannerImgLoading] = useState<number>(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm<INicheForm>({
    mode: "all",
    defaultValues: {
      name: fields[NicheFieldsNames.name].defaultValue,
      description: fields[NicheFieldsNames.description].defaultValue,
    }
  });

  const showErrorNotification = useCallback((message: string) => {
    showNotification({
      title: message,
      variant: "error"
    });
  }, []);

  const handleAvatarImageError = useCallback((message: string) => {
    handleAvatarTicketId("");
    showErrorNotification(message);
  }, [handleAvatarTicketId]);

  const handleAvatarImageLoad = useCallback((percent: number) => {
    setAvatarImgLoading(percent);
  }, []);

  const handleBannerImageError = useCallback((message: string) => {
    handleBannerTicketId("");
    showErrorNotification(message);
  }, [handleBannerTicketId]);

  const handleBannerImageLoad = useCallback((percent: number) => {
    setBannerImgLoading(percent);
  }, []);

  const isButtonDisabled = useMemo(() => {
    const isAvatarLoading = (preview && avatarImgLoading > 0) || (!preview && (avatarImgLoading > 0 || !avatarTicketId));
    const isBannerLoading = (banner && bannerImgLoading > 0) || (!banner && (bannerImgLoading > 0 || !bannerTicketId));
    const noTags = tags.length === 0;
    const noPreferences = prefOptions.every(option => !option.isChecked);
    return !isValid || isSubmitting || isAvatarLoading || isBannerLoading || noTags || noPreferences;
  }, [isValid, isSubmitting, preview, avatarImgLoading, avatarTicketId, tags, prefOptions, banner, bannerImgLoading, bannerTicketId]);

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetName = event.target.name;
    const copyOptions = [...prefOptions];

    for (const filed of copyOptions) {
      if (filed.name === targetName) {
        filed.isChecked = !filed.isChecked;
      }
    }

    onSetPreferences([...copyOptions]);
  };

  return (
    <form className="NicheForm" onSubmit={handleSubmit(onSubmit)}>
      <div className="NicheImage">
        <ImageUploader
          preview={preview || undefined}
          title="Upload an avatar"
          id="niche-image"
          uploadTicketType="niche"
          onReady={handleAvatarTicketId}
          onError={handleAvatarImageError}
          onProgress={handleAvatarImageLoad}
          minImgHeight={194}
          minImgWidth={194}
        />
      </div>

      <div className="NicheBanner">
        <ImageUploader
          preview={banner || undefined}
          title="Upload a banner"
          id="niche-banner"
          uploadTicketType="niche"
          onReady={handleBannerTicketId}
          onError={handleBannerImageError}
          onProgress={handleBannerImageLoad}
          minImgHeight={200}
          minImgWidth={880}
        />
      </div>

      {Object.values(fields).map((fieldOptions) => (
        <NicheInputField
          key={fieldOptions.id}
          errors={errors}
          register={register}
          {...fieldOptions}
        />
      ))}

      <div className="NicheTag">
        <span className="NicheInputField-Label">Select the tags that best describe your Niche's content.</span>
        <TagManager tags={tags} onChange={onTagsChange} maxTags={MAX_TAGS}/>
      </div>

      <div className="NichePreferences">
        <span className="NicheInputField-Label">Select the content preference(s) for your Niche.</span>
        <div className="NichePreferencesList">
          {prefOptions.map((filed, index) => (
            <div key={index} className="NichePreferencesItem">
              <Checkbox
                label={filed.name}
                onChange={handleCheckBoxChange}
                checked={filed.isChecked}
                name={filed.name}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="BtnWrap">
        <Button type="submit" fullWidth disabled={!!isButtonDisabled} aria-label={btnTitle}>
          {isSubmitting ? "Uploading..." : btnTitle}
        </Button>
      </div>
    </form>
  );
};

